import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/App";
import { toast } from "sonner";
import { safeRedirect } from "@/utils/api";
import BottomNav from "@/components/BottomNav";
import {
  Plane, Hotel, MapPin, Star, Clock, Tag, Search,
  Heart, Bookmark, Loader2, Info, ArrowRight, Navigation
} from "lucide-react";
import { useNearbyAirport } from "@/hooks/useNearbyAirport";

const dealTabs = [
  { id: "all", label: "All Deals" },
  { id: "flights", label: "Flights", icon: Plane },
  { id: "hotels", label: "Hotels", icon: Hotel },
  { id: "activities", label: "Activities", icon: MapPin }
];

// Popular city codes for suggestions
const POPULAR_CITIES = [
  { code: "NYC", name: "New York" },
  { code: "LON", name: "London" },
  { code: "PAR", name: "Paris" },
  { code: "TYO", name: "Tokyo" },
  { code: "DXB", name: "Dubai" },
  { code: "LAX", name: "Los Angeles" },
  { code: "MIA", name: "Miami" },
  { code: "BCN", name: "Barcelona" },
  { code: "ROM", name: "Rome" },
  { code: "SYD", name: "Sydney" },
];

export default function Deals() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [deals, setDeals] = useState({ flights: [], hotels: [], activities: [], sponsored: [] });
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("savecation_saved_deals") || "[]");
      return new Set(saved);
    } catch { return new Set(); }
  });
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [isReserving, setIsReserving] = useState(false);
  const [searchMode, setSearchMode] = useState("browse");
  const [user, setUser] = useState(null);
  const { airport, deals: nearbyDeals } = useNearbyAirport(user);
  
  // Flight search form
  const [flightSearch, setFlightSearch] = useState({
    origin: "",
    destination: "",
    departure_date: "",
    return_date: "",
    adults: 1,
    travel_class: "ECONOMY",
    non_stop: false
  });
  
  // Hotel search form
  const [hotelSearch, setHotelSearch] = useState({
    city_code: "",
    check_in_date: "",
    check_out_date: "",
    adults: 1,
    room_quantity: 1
  });
  
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [userRes, dealsRes] = await Promise.all([
        fetch(`${API}/auth/me`, { credentials: "include" }),
        fetch(`${API}/deals/all`)
      ]);
      if (userRes.ok) setUser(await userRes.json());
      if (dealsRes.ok) setDeals(await dealsRes.json());
    } catch (error) {
      console.error("Error fetching deals:", error);
      toast.error("Failed to load deals. Please refresh.");
    } finally {
      setIsLoading(false);
    }
  };

  const searchFlights = async () => {
    if (!flightSearch.origin || !flightSearch.destination || !flightSearch.departure_date) {
      toast.error("Please fill in origin, destination, and departure date");
      return;
    }
    
    setIsSearching(true);
    setSearchResults(null);
    
    try {
      const response = await fetch(`${API}/deals/search/flights`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(flightSearch)
      });
      
      const data = await response.json();
      
      if (data.deals && data.deals.length > 0) {
        setSearchResults({ type: "flights", deals: data.deals, source: data.source });
        toast.success(`Found ${data.deals.length} flights via ${data.source === "amadeus" ? "Amadeus" : "cached results"}`);
      } else {
        toast.info("No flights found for this route. Try different dates or destinations.");
        setSearchResults({ type: "flights", deals: [], source: data.source });
      }
    } catch (error) {
      console.error("Flight search error:", error);
      toast.error("Failed to search flights. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const searchHotels = async () => {
    if (!hotelSearch.city_code || !hotelSearch.check_in_date || !hotelSearch.check_out_date) {
      toast.error("Please fill in city code, check-in, and check-out dates");
      return;
    }
    
    setIsSearching(true);
    setSearchResults(null);
    
    try {
      const response = await fetch(`${API}/deals/search/hotels`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(hotelSearch)
      });
      
      const data = await response.json();
      
      if (data.deals && data.deals.length > 0) {
        setSearchResults({ type: "hotels", deals: data.deals, source: data.source });
        toast.success(`Found ${data.deals.length} hotels via ${data.source === "amadeus" ? "Amadeus" : "cached results"}`);
      } else {
        toast.info("No hotels found. Try a different city or dates.");
        setSearchResults({ type: "hotels", deals: [], source: data.source });
      }
    } catch (error) {
      console.error("Hotel search error:", error);
      toast.error("Failed to search hotels. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchResults(null);
    setSearchMode("browse");
  };
  
  const toggleFavorite = (dealId) => {
    setFavorites(prev => {
      const newFavs = new Set(prev);
      if (newFavs.has(dealId)) {
        newFavs.delete(dealId);
        toast.info("Removed from saved deals");
      } else {
        newFavs.add(dealId);
        toast.success("Deal saved! ❤️");
      }
      try { localStorage.setItem("savecation_saved_deals", JSON.stringify([...newFavs])); } catch {}
      return newFavs;
    });
  };

  const openReserveModal = async (deal) => {
    // Check auth by calling /auth/me (httpOnly cookie is sent automatically)
    try {
      const authCheck = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (!authCheck.ok) {
        toast.error("Please sign in to reserve deals");
        navigate("/auth");
        return;
      }
    } catch {
      toast.error("Please sign in to reserve deals");
      navigate("/auth");
      return;
    }
    
    const price = deal.price || deal.total_price || deal.totalPrice || (deal.price_per_night ? deal.price_per_night * (deal.nights || 7) : 0);
    if (!price || price <= 0) {
      toast.error("Price unavailable for this deal. Try another.");
      return;
    }
    setSelectedDeal({ ...deal, totalPrice: price });
    setDepositAmount(Math.ceil(price * 0.10).toString());
    setShowReserveModal(true);
  };

  const handleReserve = async () => {
    if (!selectedDeal || !depositAmount) return;
    
    const amount = parseFloat(depositAmount);
    const minDeposit = selectedDeal.totalPrice * 0.10;
    
    if (amount < minDeposit) {
      toast.error(`Minimum deposit is $${minDeposit.toFixed(2)} (10%)`);
      return;
    }
    
    setIsReserving(true);
    try {
      const response = await fetch(`${API}/payments/reserve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          deal_id: selectedDeal.deal_id,
          deposit_amount: amount,
          origin_url: window.location.origin,
          deal_details: {
            deal_id: selectedDeal.deal_id,
            name: selectedDeal.name || selectedDeal.title || `${selectedDeal.origin || ""} to ${selectedDeal.destination || ""}`.trim(),
            price: selectedDeal.totalPrice || selectedDeal.total_price || selectedDeal.price,
            totalPrice: selectedDeal.totalPrice || selectedDeal.total_price || selectedDeal.price,
            type: selectedDeal.type,
            location: selectedDeal.location || selectedDeal.destination || selectedDeal.city_code || "",
            image: selectedDeal.image,
          }
        })
      });
      
      if (response.status === 401 || response.status === 403) {
        toast.error("Please sign in to reserve deals");
        navigate("/auth");
        return;
      }
      
      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error("Server error. Please try again."); }
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to create reservation");
      }
      
      if (data?.url) {
        safeRedirect(data.url);
      } else {
        throw new Error("No checkout URL received. Please try again.");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong. Please try again.");
      setIsReserving(false);
    }
  };
  
  const getFilteredDeals = () => {
    if (activeTab === "all") {
      return [...deals.flights, ...deals.hotels, ...deals.activities];
    }
    return deals[activeTab] || [];
  };
  
  const filteredDeals = getFilteredDeals();
  
  // Get tomorrow's date for date inputs
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="deals-skeleton">
        {/* Skeleton Header */}
        <div className="bg-white border-b border-slate-100 px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="h-6 w-28 bg-slate-200 rounded animate-pulse mb-1" />
              <div className="h-3 w-40 bg-slate-100 rounded animate-pulse" />
            </div>
            <div className="h-10 w-24 bg-slate-200 rounded-full animate-pulse" />
          </div>
          {/* Skeleton tabs */}
          <div className="flex gap-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-8 w-20 bg-slate-100 rounded-full animate-pulse" />
            ))}
          </div>
        </div>
        <div className="px-6 py-6 space-y-4">
          {/* Skeleton deal cards */}
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="h-40 bg-slate-200 animate-pulse" />
              <div className="p-4">
                <div className="h-5 w-3/4 bg-slate-200 rounded animate-pulse mb-2" />
                <div className="h-3 w-1/2 bg-slate-100 rounded animate-pulse mb-3" />
                <div className="flex justify-between items-center">
                  <div className="h-6 w-20 bg-slate-200 rounded animate-pulse" />
                  <div className="h-9 w-24 bg-sky-100 rounded-full animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <BottomNav />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="deals-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4 sticky top-0 z-40">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {searchMode === "search" ? "Search Deals" : "Hot Deals"}
            </h1>
            <p className="text-sm text-slate-500">
              {searchMode === "search" ? "Real-time prices from Amadeus" : "Featured getaways"}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={searchMode === "search" ? "default" : "outline"}
              onClick={() => setSearchMode(searchMode === "search" ? "browse" : "search")}
              className="rounded-full h-10 gap-2"
              data-testid="toggle-search"
            >
              <Search className="w-4 h-4" />
              {searchMode === "search" ? "Browse" : "Search"}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/reservations")}
              className="rounded-full h-10 gap-2"
              data-testid="view-reservations"
            >
              <Bookmark className="w-4 h-4" />
              My Reservations
            </Button>
          </div>
        </div>
        
        {searchMode === "browse" && (
          <div className="deal-tabs">
            {dealTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`deal-tab flex items-center gap-2 ${activeTab === tab.id ? "active" : "text-slate-500"}`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.icon && <tab.icon className="w-4 h-4" />}
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </header>
      
      <main className="px-6 py-6">
        {/* Search Mode */}
        {searchMode === "search" && (
          <div className="mb-6">
            <Tabs defaultValue="flights" className="w-full">
              <TabsList className="grid w-full grid-cols-2 rounded-2xl bg-slate-100 p-1 h-12">
                <TabsTrigger value="flights" className="rounded-xl data-[state=active]:bg-white">
                  <Plane className="w-4 h-4 mr-2" /> Flights
                </TabsTrigger>
                <TabsTrigger value="hotels" className="rounded-xl data-[state=active]:bg-white">
                  <Hotel className="w-4 h-4 mr-2" /> Hotels
                </TabsTrigger>
              </TabsList>
              
              {/* Flight Search Form */}
              <TabsContent value="flights" className="mt-4">
                <div className="bg-white rounded-3xl border-2 border-slate-100 p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">From</label>
                      <Input
                        placeholder="NYC, LAX, MIA..."
                        value={flightSearch.origin}
                        onChange={(e) => setFlightSearch({...flightSearch, origin: e.target.value.toUpperCase()})}
                        className="savecation-input uppercase"
                        maxLength={3}
                        data-testid="flight-origin"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">To</label>
                      <Input
                        placeholder="LON, PAR, TYO..."
                        value={flightSearch.destination}
                        onChange={(e) => setFlightSearch({...flightSearch, destination: e.target.value.toUpperCase()})}
                        className="savecation-input uppercase"
                        maxLength={3}
                        data-testid="flight-destination"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Departure</label>
                      <Input
                        type="date"
                        value={flightSearch.departure_date}
                        onChange={(e) => setFlightSearch({...flightSearch, departure_date: e.target.value})}
                        min={minDate}
                        className="savecation-input"
                        data-testid="flight-departure"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Return (optional)</label>
                      <Input
                        type="date"
                        value={flightSearch.return_date}
                        onChange={(e) => setFlightSearch({...flightSearch, return_date: e.target.value})}
                        min={flightSearch.departure_date || minDate}
                        className="savecation-input"
                        data-testid="flight-return"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Travelers</label>
                      <Select 
                        value={flightSearch.adults.toString()} 
                        onValueChange={(v) => setFlightSearch({...flightSearch, adults: parseInt(v)})}
                      >
                        <SelectTrigger className="savecation-input">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5,6].map(n => (
                            <SelectItem key={n} value={n.toString()}>{n} Adult{n > 1 ? "s" : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Class</label>
                      <Select 
                        value={flightSearch.travel_class} 
                        onValueChange={(v) => setFlightSearch({...flightSearch, travel_class: v})}
                      >
                        <SelectTrigger className="savecation-input">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ECONOMY">Economy</SelectItem>
                          <SelectItem value="PREMIUM_ECONOMY">Premium</SelectItem>
                          <SelectItem value="BUSINESS">Business</SelectItem>
                          <SelectItem value="FIRST">First</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={flightSearch.non_stop}
                          onChange={(e) => setFlightSearch({...flightSearch, non_stop: e.target.checked})}
                          className="rounded border-slate-300"
                        />
                        <span className="text-sm text-slate-600">Non-stop only</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Popular routes */}
                  <div className="pt-2">
                    <p className="text-xs text-slate-500 mb-2">Popular destinations:</p>
                    <div className="flex flex-wrap gap-2">
                      {POPULAR_CITIES.slice(0, 5).map(city => (
                        <button
                          key={city.code}
                          onClick={() => setFlightSearch({...flightSearch, destination: city.code})}
                          className="px-3 py-1 text-xs bg-slate-100 hover:bg-sky-100 text-slate-600 hover:text-sky-600 rounded-full transition-colors"
                        >
                          {city.name} ({city.code})
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <Button
                    onClick={searchFlights}
                    disabled={isSearching}
                    className="savecation-button w-full"
                    data-testid="search-flights-btn"
                  >
                    {isSearching ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Searching Amadeus...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Search className="w-5 h-5" />
                        Search Flights
                      </span>
                    )}
                  </Button>
                </div>
              </TabsContent>
              
              {/* Hotel Search Form */}
              <TabsContent value="hotels" className="mt-4">
                <div className="bg-white rounded-3xl border-2 border-slate-100 p-6 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">City Code</label>
                    <Input
                      placeholder="PAR, LON, NYC..."
                      value={hotelSearch.city_code}
                      onChange={(e) => setHotelSearch({...hotelSearch, city_code: e.target.value.toUpperCase()})}
                      className="savecation-input uppercase"
                      maxLength={3}
                      data-testid="hotel-city"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Check-in</label>
                      <Input
                        type="date"
                        value={hotelSearch.check_in_date}
                        onChange={(e) => setHotelSearch({...hotelSearch, check_in_date: e.target.value})}
                        min={minDate}
                        className="savecation-input"
                        data-testid="hotel-checkin"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Check-out</label>
                      <Input
                        type="date"
                        value={hotelSearch.check_out_date}
                        onChange={(e) => setHotelSearch({...hotelSearch, check_out_date: e.target.value})}
                        min={hotelSearch.check_in_date || minDate}
                        className="savecation-input"
                        data-testid="hotel-checkout"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Guests</label>
                      <Select 
                        value={hotelSearch.adults.toString()} 
                        onValueChange={(v) => setHotelSearch({...hotelSearch, adults: parseInt(v)})}
                      >
                        <SelectTrigger className="savecation-input">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5,6].map(n => (
                            <SelectItem key={n} value={n.toString()}>{n} Guest{n > 1 ? "s" : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Rooms</label>
                      <Select 
                        value={hotelSearch.room_quantity.toString()} 
                        onValueChange={(v) => setHotelSearch({...hotelSearch, room_quantity: parseInt(v)})}
                      >
                        <SelectTrigger className="savecation-input">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4].map(n => (
                            <SelectItem key={n} value={n.toString()}>{n} Room{n > 1 ? "s" : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Popular cities */}
                  <div className="pt-2">
                    <p className="text-xs text-slate-500 mb-2">Popular cities:</p>
                    <div className="flex flex-wrap gap-2">
                      {POPULAR_CITIES.slice(0, 5).map(city => (
                        <button
                          key={city.code}
                          onClick={() => setHotelSearch({...hotelSearch, city_code: city.code})}
                          className="px-3 py-1 text-xs bg-slate-100 hover:bg-sky-100 text-slate-600 hover:text-sky-600 rounded-full transition-colors"
                        >
                          {city.name} ({city.code})
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <Button
                    onClick={searchHotels}
                    disabled={isSearching}
                    className="savecation-button w-full"
                    data-testid="search-hotels-btn"
                  >
                    {isSearching ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Searching Amadeus...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Search className="w-5 h-5" />
                        Search Hotels
                      </span>
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
            
            {/* Search Results */}
            {searchResults && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">
                      {searchResults.deals.length} {searchResults.type === "flights" ? "Flights" : "Hotels"} Found
                    </h2>
                    <p className="text-sm text-slate-500">
                      Source: {searchResults.source === "amadeus" ? "🌐 Amadeus Live" : searchResults.source === "amadeus_cached" ? "⚡ Cached" : "📋 Featured"}
                    </p>
                  </div>
                  <Button variant="outline" onClick={clearSearch} className="rounded-full">
                    Clear Results
                  </Button>
                </div>
                
                {searchResults.deals.length === 0 ? (
                  <div className="bg-slate-50 rounded-2xl p-10 text-center">
                    <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-bold text-slate-700 mb-1">No results found</h3>
                    <p className="text-sm text-slate-500 mb-4">Try adjusting your dates, destination, or search for a nearby city.</p>
                    <div className="flex flex-wrap justify-center gap-2 mb-4">
                      {POPULAR_CITIES.slice(0, 4).map(c => (
                        <span key={c.code} className="px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-xs font-semibold">{c.name}</span>
                      ))}
                    </div>
                    <Button variant="outline" onClick={clearSearch} className="rounded-full">
                      Clear Search
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {searchResults.deals.map((deal, index) => (
                      <SearchResultCard
                        key={deal.deal_id || index}
                        deal={deal}
                        type={searchResults.type}
                        onReserve={() => openReserveModal(deal)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Browse Mode */}
        {searchMode === "browse" && (
          <>
            {/* Reserve & Save Banner */}
            <div className="bg-gradient-to-r from-sky-500 to-sky-600 rounded-2xl p-4 mb-6 text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Bookmark className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">Reserve & Save</h3>
                  <p className="text-sm text-sky-100">Lock in prices with just 10% deposit!</p>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => setSearchMode("search")}
                  className="rounded-full bg-white/20 hover:bg-white/30 text-white border-0"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search Live
                </Button>
              </div>
            </div>

            {/* Featured Deals Info */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-6">
              <p className="text-sm text-slate-600">
                <strong>✨ Featured Deals</strong> - Click "Search" to find real-time prices powered by Amadeus API.
              </p>
            </div>
            
            {/* Sponsored / Featured Deals */}
            {deals.sponsored?.length > 0 && activeTab === "all" && (
              <section className="mb-8" data-testid="sponsored-deals-section">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="w-5 h-5 text-amber-500" />
                  <h2 className="text-lg font-bold text-slate-900">Sponsored Deals</h2>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
                  {deals.sponsored.map(deal => (
                    <div key={deal.deal_id} className="flex-shrink-0 w-72 rounded-2xl overflow-hidden border-2 border-amber-200 bg-white" data-testid={`sponsored-${deal.deal_id}`}>
                      <div className="relative">
                        <img src={deal.image} alt={deal.name} className="w-full h-36 object-cover" />
                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-400 text-white">
                          {deal.badge}
                        </span>
                        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold bg-rose-500 text-white">
                          -{deal.discount_pct}%
                        </span>
                      </div>
                      <div className="p-4">
                        <p className="text-xs text-amber-600 font-medium mb-1">{deal.sponsor}</p>
                        <h3 className="font-bold text-slate-900 mb-1">{deal.name}</h3>
                        <p className="text-xs text-slate-500 mb-3">{deal.description}</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-sky-600">${deal.price}</span>
                            <span className="text-sm text-slate-400 line-through ml-2">${deal.original_price}</span>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => openReserveModal(deal)}
                            className="rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs h-8 px-4"
                            data-testid={`reserve-sponsored-${deal.deal_id}`}
                          >
                            Reserve
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
            
            {/* Deals Near You */}
            {nearbyDeals.length > 0 && activeTab === "all" && (
              <section className="mb-8" data-testid="nearby-deals-browse">
                <div className="flex items-center gap-2 mb-4">
                  <Navigation className="w-5 h-5 text-sky-500" />
                  <h2 className="text-lg font-bold text-slate-900">
                    Deals from {airport?.city || airport?.code}
                  </h2>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
                  {nearbyDeals.map(deal => (
                    <div
                      key={deal.deal_id}
                      className="flex-shrink-0 w-56 rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow snap-start cursor-pointer"
                      data-testid={`nearby-browse-${deal.destination}`}
                    >
                      <div className="relative h-32">
                        <img src={deal.image} alt={deal.city || deal.destination} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-2 left-3">
                          <p className="text-white font-bold">{deal.city || deal.destination}</p>
                          <p className="text-white/70 text-xs">{deal.country || ""}</p>
                        </div>
                      </div>
                      <div className="p-3 flex items-center justify-between">
                        <div>
                          <span className="text-lg font-bold text-emerald-600">${deal.price}</span>
                          <span className="text-xs text-slate-400 ml-1">round trip</span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => openReserveModal(deal)}
                          className="rounded-full bg-sky-500 hover:bg-sky-600 text-white text-xs h-7 px-3"
                          data-testid={`reserve-nearby-${deal.destination}`}
                        >
                          Reserve
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Deals Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-24">
              {filteredDeals.map(deal => (
                <DealCard 
                  key={deal.deal_id} 
                  deal={deal} 
                  isFavorite={favorites.has(deal.deal_id)}
                  onToggleFavorite={() => toggleFavorite(deal.deal_id)}
                  onReserve={() => openReserveModal(deal)}
                />
              ))}
            </div>
          </>
        )}
      </main>

      {/* Reserve Modal */}
      <Dialog open={showReserveModal} onOpenChange={setShowReserveModal}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Bookmark className="w-6 h-6 text-sky-500" />
              Reserve This Deal
            </DialogTitle>
          </DialogHeader>
          {selectedDeal && (
            <div className="space-y-4 mt-4">
              {/* Deal Summary */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl">
                <img 
                  src={selectedDeal.image} 
                  alt={selectedDeal.name || selectedDeal.destination}
                  className="w-16 h-16 rounded-xl object-cover"
                />
                <div>
                  <h3 className="font-bold text-slate-900">
                    {selectedDeal.name || `${selectedDeal.origin} → ${selectedDeal.destination}`}
                  </h3>
                  <p className="text-lg font-bold text-sky-600">
                    ${selectedDeal.totalPrice?.toFixed(2)} total
                  </p>
                </div>
              </div>

              {/* How it works */}
              <div className="bg-sky-50 rounded-2xl p-4">
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-sky-500" />
                  How Reserve & Save Works
                </h4>
                <ol className="text-sm text-slate-600 space-y-1">
                  <li>1. Pay a small deposit (min 10%) to lock in this price</li>
                  <li>2. Continue saving in your goals</li>
                  <li>3. Pay the rest when you&apos;re ready (up to 30 days)</li>
                </ol>
              </div>

              {/* Deposit Amount */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Deposit Amount (min ${(selectedDeal.totalPrice * 0.10).toFixed(2)})
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    className="savecation-input pl-8"
                    data-testid="deposit-amount-input"
                  />
                </div>
              </div>

              {/* Quick amounts */}
              <div className="flex gap-2">
                {[10, 25, 50].map(percent => (
                  <Button
                    key={percent}
                    variant="outline"
                    onClick={() => setDepositAmount(Math.ceil(selectedDeal.totalPrice * percent / 100).toString())}
                    className="flex-1 rounded-full"
                  >
                    {percent}%
                  </Button>
                ))}
              </div>

              {/* Summary */}
              <div className="border-t border-slate-100 pt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-500">Deposit now</span>
                  <span className="font-semibold">${parseFloat(depositAmount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Pay later</span>
                  <span className="font-semibold">
                    ${(selectedDeal.totalPrice - parseFloat(depositAmount || 0)).toFixed(2)}
                  </span>
                </div>
              </div>

              <Button
                onClick={handleReserve}
                disabled={isReserving || !depositAmount}
                className="savecation-button w-full"
                data-testid="confirm-reserve-btn"
              >
                {isReserving ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Bookmark className="w-5 h-5" />
                    Reserve for ${parseFloat(depositAmount || 0).toFixed(2)}
                  </span>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      <BottomNav />
    </div>
  );
}

function DealCard({ deal, isFavorite, onToggleFavorite, onReserve }) {
  const renderTypeIcon = () => {
    const iconClass = "w-3 h-3 inline mr-1";
    switch (deal.type) {
      case "flight": return <Plane className={iconClass} />;
      case "hotel": return <Hotel className={iconClass} />;
      case "activity": return <MapPin className={iconClass} />;
      default: return <Tag className={iconClass} />;
    }
  };
  
  return (
    <div className="deal-card bg-white border-2 border-slate-100 overflow-hidden" data-testid={`deal-${deal.deal_id}`}>
      {/* Image */}
      <div className="relative aspect-video">
        <img 
          src={deal.image} 
          alt={deal.name || deal.destination}
          className="w-full h-full object-cover"
        />
        <button 
          onClick={onToggleFavorite}
          className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center ${
            isFavorite ? "bg-rose-500 text-white" : "bg-white/90 text-slate-600"
          }`}
          data-testid={`favorite-${deal.deal_id}`}
        >
          <Heart className={`w-5 h-5 ${isFavorite ? "fill-current" : ""}`} />
        </button>
        <div className="deal-badge">
          {renderTypeIcon()}
          {deal.type.charAt(0).toUpperCase() + deal.type.slice(1)}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-5">
        {deal.type === "flight" && (
          <>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
              <span className="font-semibold text-slate-900">{deal.origin}</span>
              <Plane className="w-4 h-4" />
              <span className="font-semibold text-slate-900">{deal.destination}</span>
            </div>
            <p className="text-sm text-slate-500 mb-3">{deal.airline} • {deal.departure}</p>
          </>
        )}
        
        {deal.type === "hotel" && (
          <>
            <h3 className="font-bold text-slate-900 mb-1">{deal.name}</h3>
            <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
              <MapPin className="w-3 h-3" /> {deal.location}
            </p>
            <div className="flex items-center gap-1 text-sm text-amber-500 mb-3">
              <Star className="w-4 h-4 fill-current" /> {deal.rating}
            </div>
          </>
        )}
        
        {deal.type === "activity" && (
          <>
            <h3 className="font-bold text-slate-900 mb-1">{deal.name}</h3>
            <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
              <MapPin className="w-3 h-3" /> {deal.location}
            </p>
            <div className="flex items-center gap-3 text-sm text-slate-500 mb-3">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {deal.duration}
              </span>
              <span className="flex items-center gap-1 text-amber-500">
                <Star className="w-3 h-3 fill-current" /> {deal.rating}
              </span>
            </div>
          </>
        )}
        
        <div className="flex items-center justify-between">
          <div>
            <span className="text-2xl font-bold text-sky-600">${deal.price || deal.price_per_night}</span>
            {deal.type === "hotel" && <span className="text-sm text-slate-500">/night</span>}
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={onReserve}
              className="h-10 px-4 rounded-full border-sky-200 text-sky-600 hover:bg-sky-50"
              data-testid={`reserve-${deal.deal_id}`}
            >
              <Bookmark className="w-4 h-4 mr-1" /> Reserve
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Search Result Card for Amadeus results
function SearchResultCard({ deal, type, onReserve }) {
  const price = deal.price || deal.price_per_night || deal.total_price || 0;
  
  return (
    <div className="bg-white rounded-3xl border-2 border-slate-100 overflow-hidden hover:border-sky-200 transition-colors" data-testid={`search-result-${deal.deal_id}`}>
      {/* Image */}
      <div className="relative h-40">
        <img 
          src={deal.image} 
          alt={deal.name || deal.destination || "Deal"}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-sky-500 text-white text-xs font-semibold flex items-center gap-1">
          {type === "flights" ? <Plane className="w-3 h-3" /> : <Hotel className="w-3 h-3" />}
          {type === "flights" ? "Flight" : "Hotel"}
        </div>
        {deal.stops !== undefined && (
          <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-white/90 text-slate-700 text-xs font-semibold">
            {deal.stops === 0 ? "Non-stop" : `${deal.stops} stop${deal.stops > 1 ? "s" : ""}`}
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-5">
        {type === "flights" ? (
          <>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-lg font-bold text-slate-900">{deal.origin}</span>
              <ArrowRight className="w-5 h-5 text-sky-500" />
              <span className="text-lg font-bold text-slate-900">{deal.destination}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
              <span>{deal.airline}</span>
              {deal.carrier_code && <span className="text-slate-400">({deal.carrier_code})</span>}
              <span>•</span>
              <span>{deal.departure}</span>
            </div>
            {deal.duration && (
              <p className="text-xs text-slate-400 mb-3">Duration: {deal.duration}</p>
            )}
          </>
        ) : (
          <>
            <h3 className="font-bold text-slate-900 mb-1 line-clamp-1">{deal.name}</h3>
            <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
              <MapPin className="w-3 h-3" /> {deal.location || deal.city_code}
            </p>
            {deal.rating && (
              <div className="flex items-center gap-1 text-sm text-amber-500 mb-2">
                <Star className="w-4 h-4 fill-current" /> {deal.rating}
              </div>
            )}
            {deal.room_type && (
              <p className="text-xs text-slate-400 mb-2 line-clamp-1">{deal.room_type}</p>
            )}
            {deal.nights && (
              <p className="text-xs text-slate-500">
                {deal.nights} night{deal.nights > 1 ? "s" : ""} • Total: ${deal.total_price?.toFixed(2)}
              </p>
            )}
          </>
        )}
        
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <div>
            <span className="text-2xl font-bold text-sky-600">${price.toFixed ? price.toFixed(0) : price}</span>
            {type === "hotels" && <span className="text-sm text-slate-500">/night</span>}
          </div>
          <Button 
            onClick={onReserve}
            className="savecation-button h-10"
            data-testid={`reserve-search-${deal.deal_id}`}
          >
            <Bookmark className="w-4 h-4 mr-1" /> Reserve
          </Button>
        </div>
      </div>
    </div>
  );
}
